import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, CalendarDays, Scissors, Clock, FileText, Users, UserPlus, Plus, Trash2, Save, X, BarChart2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lordag", "Sondag"];
const TIME_OPTIONS = [];
for (let h = 8; h <= 20; h++) {
  TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 20) TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:30`);
}
const STATUS_LABELS = { confirmed: "Bekreftet", cancelled: "Avbestilt", completed: "Fullfort" };
const STATUS_COLORS = { confirmed: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800", completed: "bg-blue-100 text-blue-800" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [content, setContent] = useState({});
  const [customers, setCustomers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState(null);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const authFetch = useCallback(async (method, url, data = null) => {
    try {
      const config = { method, url: `${API}${url}`, headers: authHeaders() };
      if (data) config.data = data;
      return await axios(config);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      throw err;
    }
  }, [authHeaders, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [b, s, sc, co, cu, a, st] = await Promise.all([
        authFetch("get", "/bookings"), authFetch("get", "/services/all"),
        axios.get(`${API}/schedule`), axios.get(`${API}/content`),
        authFetch("get", "/customers"), authFetch("get", "/auth/admins"),
        authFetch("get", "/stats"),
      ]);
      setBookings(b.data); setServices(s.data); setSchedule(sc.data);
      setContent(co.data); setCustomers(cu.data); setAdmins(a.data); setStats(st.data);
    } catch {}
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-14">
          <h1 className="font-serif text-xl">Imassasje Admin</h1>
          <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={logout} className="text-muted-foreground hover:text-foreground">
            <LogOut size={16} className="mr-2" /> Logg ut
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-2xl p-5 shadow-sm border border-border/40">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><BarChart2 size={12} /> Besøk i dag</p>
              <p className="text-3xl font-serif">{stats.visits_today}</p>
            </div>
            <div className="bg-card rounded-2xl p-5 shadow-sm border border-border/40">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><BarChart2 size={12} /> Besøk denne måneden</p>
              <p className="text-3xl font-serif">{stats.visits_month}</p>
            </div>
            <div className="bg-card rounded-2xl p-5 shadow-sm border border-border/40">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><CalendarDays size={12} /> Bestillinger denne måneden</p>
              <p className="text-3xl font-serif">{stats.bookings_month}</p>
            </div>
            <div className="bg-card rounded-2xl p-5 shadow-sm border border-border/40">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><Scissors size={12} /> Mest populære tjeneste</p>
              <p className="text-sm font-medium mt-1 leading-snug">{stats.most_popular_service ?? "—"}</p>
            </div>
          </div>
        )}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList data-testid="admin-tabs" className="bg-muted p-1 rounded-xl flex-wrap h-auto gap-1">
            <TabsTrigger value="bookings" className="rounded-lg text-xs md:text-sm"><CalendarDays size={14} className="mr-1.5" />Bookinger</TabsTrigger>
            <TabsTrigger value="services" className="rounded-lg text-xs md:text-sm"><Scissors size={14} className="mr-1.5" />Tjenester</TabsTrigger>
            <TabsTrigger value="schedule" className="rounded-lg text-xs md:text-sm"><Clock size={14} className="mr-1.5" />Timeplan</TabsTrigger>
            <TabsTrigger value="content" className="rounded-lg text-xs md:text-sm"><FileText size={14} className="mr-1.5" />Innhold</TabsTrigger>
            <TabsTrigger value="customers" className="rounded-lg text-xs md:text-sm"><Users size={14} className="mr-1.5" />Kunder</TabsTrigger>
            <TabsTrigger value="admins" className="rounded-lg text-xs md:text-sm"><UserPlus size={14} className="mr-1.5" />Adminer</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <BookingsTab bookings={bookings} authFetch={authFetch} onRefresh={loadAll} />
          </TabsContent>
          <TabsContent value="services">
            <ServicesTab services={services} authFetch={authFetch} onRefresh={loadAll} />
          </TabsContent>
          <TabsContent value="schedule">
            <ScheduleTab schedule={schedule} authFetch={authFetch} onRefresh={loadAll} />
          </TabsContent>
          <TabsContent value="content">
            <ContentTab content={content} authFetch={authFetch} onRefresh={loadAll} />
          </TabsContent>
          <TabsContent value="customers">
            <CustomersTab customers={customers} services={services} schedule={schedule} authFetch={authFetch} onRefresh={loadAll} />
          </TabsContent>
          <TabsContent value="admins">
            <AdminsTab admins={admins} authFetch={authFetch} onRefresh={loadAll} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ============ BOOKINGS TAB ============ */
function BookingsTab({ bookings, authFetch, onRefresh }) {
  const [editingComment, setEditingComment] = useState(null);
  const [commentText, setCommentText] = useState("");

  const updateStatus = async (id, status) => {
    try {
      await authFetch("put", `/bookings/${id}/status`, { status });
      toast.success("Status oppdatert");
      onRefresh();
    } catch { toast.error("Kunne ikke oppdatere status"); }
  };
  const deleteBooking = async (id) => {
    try {
      await authFetch("delete", `/bookings/${id}`);
      toast.success("Booking slettet");
      onRefresh();
    } catch { toast.error("Kunne ikke slette booking"); }
  };
  const saveComment = async (id) => {
    try {
      await authFetch("put", `/bookings/${id}/comment`, { admin_comment: commentText });
      toast.success("Kommentar lagret");
      setEditingComment(null);
      onRefresh();
    } catch { toast.error("Kunne ikke lagre kommentar"); }
  };

  return (
    <div data-testid="bookings-tab">
      <h2 className="font-serif text-2xl mb-4">Bookinger ({bookings.length})</h2>
      {bookings.length === 0 ? <p className="text-muted-foreground">Ingen bookinger enna.</p> : (
        <div className="space-y-3">
          {bookings.map(b => (
            <div key={b.id} data-testid={`booking-item-${b.id}`} className="bg-card rounded-xl p-4 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{b.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{b.customer_email} &middot; {b.customer_phone}</p>
                </div>
                <div className="text-sm"><span className="text-muted-foreground">Tjeneste:</span> {b.service_name}</div>
                <div className="text-sm"><span className="text-muted-foreground">Dato:</span> {b.date} kl. {b.time}</div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || "bg-muted"}`}>
                  {STATUS_LABELS[b.status] || b.status}
                </span>
                <div className="flex items-center gap-2">
                  <Select onValueChange={(v) => updateStatus(b.id, v)}>
                    <SelectTrigger data-testid={`status-select-${b.id}`} className="w-32 h-8 text-xs rounded-lg"><SelectValue placeholder="Endre" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Bekreftet</SelectItem>
                      <SelectItem value="cancelled">Avbestilt</SelectItem>
                      <SelectItem value="completed">Fullfort</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button data-testid={`delete-booking-${b.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteBooking(b.id)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              {/* Customer notes */}
              {b.notes && <p className="text-xs text-muted-foreground mt-2 italic">Kundens merknad: {b.notes}</p>}
              {/* Admin comment */}
              <div className="mt-3 pt-3 border-t border-border/50">
                {editingComment === b.id ? (
                  <div className="flex gap-2 items-start">
                    <Textarea
                      data-testid={`comment-input-${b.id}`}
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      placeholder="Skriv en intern kommentar..."
                      className="flex-1 text-sm min-h-[60px]"
                    />
                    <div className="flex flex-col gap-1">
                      <Button data-testid={`save-comment-${b.id}`} size="sm" className="rounded-lg h-8" onClick={() => saveComment(b.id)}>
                        <Save size={12} />
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-lg h-8" onClick={() => setEditingComment(null)}>
                        <X size={12} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    data-testid={`edit-comment-${b.id}`}
                    onClick={() => { setEditingComment(b.id); setCommentText(b.admin_comment || ""); }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
                  >
                    <FileText size={12} />
                    {b.admin_comment ? <span>Kommentar: {b.admin_comment}</span> : <span className="italic">Legg til kommentar...</span>}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ SERVICES TAB ============ */
function ServicesTab({ services, authFetch, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: 750, duration: 45, image_url: "" });

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", price: 750, duration: 45, image_url: "" }); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, description: s.description, price: s.price, duration: s.duration, image_url: s.image_url }); setDialogOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await authFetch("put", `/services/${editing.id}`, form);
        toast.success("Tjeneste oppdatert");
      } else {
        await authFetch("post", "/services", form);
        toast.success("Tjeneste opprettet");
      }
      setDialogOpen(false);
      onRefresh();
    } catch { toast.error("Kunne ikke lagre tjeneste"); }
  };

  const toggleActive = async (s) => {
    try {
      await authFetch("put", `/services/${s.id}`, { active: !s.active });
      onRefresh();
    } catch { toast.error("Feil"); }
  };

  const deleteService = async (id) => {
    try { await authFetch("delete", `/services/${id}`); toast.success("Slettet"); onRefresh(); }
    catch { toast.error("Feil"); }
  };

  return (
    <div data-testid="services-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl">Tjenester</h2>
        <Button data-testid="add-service-btn" size="sm" onClick={openNew} className="rounded-lg"><Plus size={14} className="mr-1" /> Ny tjeneste</Button>
      </div>
      <div className="space-y-3">
        {services.map(s => (
          <div key={s.id} data-testid={`service-item-${s.id}`} className="bg-card rounded-xl p-4 flex items-center gap-4 shadow-sm">
            {s.image_url && <img src={s.image_url} alt={s.name} className="w-16 h-16 rounded-lg object-cover" />}
            <div className="flex-1">
              <p className="font-medium">{s.name}</p>
              <p className="text-xs text-muted-foreground">{s.price} kr &middot; {s.duration} min</p>
            </div>
            <Switch data-testid={`toggle-service-${s.id}`} checked={s.active} onCheckedChange={() => toggleActive(s)} />
            <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="text-xs">Rediger</Button>
            <Button variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => deleteService(s.id)}><Trash2 size={14} /></Button>
          </div>
        ))}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">{editing ? "Rediger tjeneste" : "Ny tjeneste"}</DialogTitle>
            <DialogDescription>Fyll ut informasjon om behandlingen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="service-name-input" placeholder="Navn" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <Textarea data-testid="service-desc-input" placeholder="Beskrivelse" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="service-price-input" type="number" placeholder="Pris (kr)" value={form.price} onChange={e => setForm({ ...form, price: parseInt(e.target.value) || 0 })} />
              <Input data-testid="service-duration-input" type="number" placeholder="Varighet (min)" value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Bilde</p>
              {services.filter(s => s.image_url).length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Velg et eksisterende bilde:</p>
                  <div className="grid grid-cols-4 gap-2">
                    {services.filter(s => s.image_url).map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setForm({ ...form, image_url: s.image_url })}
                        className={`rounded-lg overflow-hidden border-2 transition-colors ${form.image_url === s.image_url ? "border-primary" : "border-transparent hover:border-muted-foreground"}`}
                        title={s.name}
                      >
                        <img src={s.image_url} alt={s.name} className="w-full h-16 object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Input data-testid="service-image-input" placeholder="Eller lim inn bilde-URL" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} />
              {form.image_url && (
                <img src={form.image_url} alt="Forhåndsvisning" className="w-full h-32 object-cover rounded-lg" />
              )}
            </div>
            <Button data-testid="save-service-btn" onClick={save} className="w-full rounded-lg">Lagre</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ SCHEDULE TAB ============ */
function ScheduleTab({ schedule, authFetch, onRefresh }) {
  const [localSchedule, setLocalSchedule] = useState([]);
  useEffect(() => { setLocalSchedule(schedule.map(s => ({ ...s }))); }, [schedule]);

  const toggleDay = (idx) => {
    const updated = [...localSchedule];
    updated[idx] = { ...updated[idx], active: !updated[idx].active };
    setLocalSchedule(updated);
  };

  const addTime = (idx, time) => {
    const updated = [...localSchedule];
    if (!updated[idx].times.includes(time)) {
      updated[idx] = { ...updated[idx], times: [...updated[idx].times, time].sort() };
      setLocalSchedule(updated);
    }
  };

  const removeTime = (idx, time) => {
    const updated = [...localSchedule];
    updated[idx] = { ...updated[idx], times: updated[idx].times.filter(t => t !== time) };
    setLocalSchedule(updated);
  };

  const saveDay = async (day) => {
    try {
      await authFetch("put", "/schedule", { day_of_week: day.day_of_week, times: day.times, active: day.active });
      toast.success(`${DAY_NAMES[day.day_of_week]} lagret`);
      onRefresh();
    } catch { toast.error("Kunne ikke lagre"); }
  };

  return (
    <div data-testid="schedule-tab">
      <h2 className="font-serif text-2xl mb-4">Ukentlig timeplan</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {localSchedule.map((day, idx) => (
          <div key={day.id} data-testid={`schedule-day-${day.day_of_week}`} className="bg-card rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium">{DAY_NAMES[day.day_of_week]}</span>
              <Switch data-testid={`toggle-day-${day.day_of_week}`} checked={day.active} onCheckedChange={() => toggleDay(idx)} />
            </div>
            {day.active && (
              <>
                <div className="flex flex-wrap gap-2 mb-3">
                  {day.times.map(t => (
                    <span key={t} className="bg-muted rounded-lg px-3 py-1 text-xs flex items-center gap-1">
                      {t}
                      <button data-testid={`remove-time-${day.day_of_week}-${t}`} onClick={() => removeTime(idx, t)} className="text-destructive hover:text-destructive/80"><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Select onValueChange={(v) => addTime(idx, v)}>
                    <SelectTrigger data-testid={`add-time-select-${day.day_of_week}`} className="h-8 text-xs rounded-lg flex-1"><SelectValue placeholder="Legg til tid" /></SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.filter(t => !day.times.includes(t)).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <Button data-testid={`save-day-${day.day_of_week}`} size="sm" className="mt-3 w-full rounded-lg" onClick={() => saveDay(day)}>
              <Save size={12} className="mr-1" /> Lagre
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ CONTENT TAB ============ */
function ContentTab({ content, authFetch, onRefresh }) {
  const [local, setLocal] = useState({});
  useEffect(() => { setLocal({ ...content }); }, [content]);

  const saveField = async (key) => {
    try {
      await authFetch("put", "/content", { key, value: local[key] || "" });
      toast.success("Lagret");
      onRefresh();
    } catch { toast.error("Feil"); }
  };

  const sections = [
    { title: "Forsiden - Hero", fields: [
      { key: "hero_title", label: "Tittel", type: "input" },
      { key: "hero_subtitle", label: "Undertittel", type: "input" },
      { key: "hero_description", label: "Beskrivelse", type: "textarea" },
      { key: "hero_image", label: "Bilde-URL", type: "input" },
      { key: "hero_label", label: "Overskrift-etikett (liten tekst)", type: "input" },
    ]},
    { title: "Knappetekster", fields: [
      { key: "btn_hero", label: "Hero-knapp (forsiden)", type: "input" },
      { key: "btn_about", label: "Om meg-knapp", type: "input" },
      { key: "btn_cta", label: "CTA-knapp (booking-seksjon)", type: "input" },
      { key: "btn_nav", label: "Navigasjonsknapp", type: "input" },
      { key: "service_card_cta", label: "Tjenestekort-lenketekst", type: "input" },
    ]},
    { title: "Tjenester-seksjon", fields: [
      { key: "services_label", label: "Etikett (liten tekst)", type: "input" },
      { key: "services_title", label: "Tittel", type: "input" },
    ]},
    { title: "Om meg", fields: [
      { key: "about_title", label: "Tittel", type: "input" },
      { key: "about_text", label: "Tekst", type: "textarea" },
      { key: "about_image", label: "Bilde-URL", type: "input" },
      { key: "about_label", label: "Etikett (liten tekst)", type: "input" },
    ]},
    { title: "Booking-seksjon", fields: [
      { key: "booking_title", label: "Tittel", type: "input" },
      { key: "booking_description", label: "Beskrivelse", type: "textarea" },
    ]},
    { title: "Kontaktinformasjon", fields: [
      { key: "contact_phone", label: "Telefon", type: "input" },
      { key: "contact_email", label: "E-post", type: "input" },
      { key: "contact_address", label: "Adresse", type: "input" },
    ]},
    { title: "Booking-skjema", fields: [
      { key: "booking_step1_title", label: "Steg 1 tittel", type: "input" },
      { key: "booking_step1_description", label: "Steg 1 beskrivelse", type: "input" },
      { key: "booking_step2_title", label: "Steg 2 tittel", type: "input" },
      { key: "booking_step2_description", label: "Steg 2 beskrivelse", type: "input" },
      { key: "booking_step3_title", label: "Steg 3 tittel", type: "input" },
      { key: "booking_step3_description", label: "Steg 3 beskrivelse", type: "input" },
      { key: "booking_step4_title", label: "Steg 4 tittel", type: "input" },
      { key: "booking_step4_description", label: "Steg 4 beskrivelse", type: "input" },
      { key: "booking_progress_service", label: "Progress: Behandling", type: "input" },
      { key: "booking_progress_datetime", label: "Progress: Dato & tid", type: "input" },
      { key: "booking_progress_details", label: "Progress: Dine detaljer", type: "input" },
      { key: "booking_progress_confirmation", label: "Progress: Bekreftelse", type: "input" },
      { key: "booking_label_name", label: "Navn-etikett", type: "input" },
      { key: "booking_placeholder_name", label: "Navn-placeholder", type: "input" },
      { key: "booking_label_email", label: "E-post-etikett", type: "input" },
      { key: "booking_placeholder_email", label: "E-post-placeholder", type: "input" },
      { key: "booking_label_phone", label: "Telefon-etikett", type: "input" },
      { key: "booking_placeholder_phone", label: "Telefon-placeholder", type: "input" },
      { key: "booking_label_address", label: "Adresse-etikett", type: "input" },
      { key: "booking_placeholder_address", label: "Adresse-placeholder", type: "input" },
      { key: "booking_label_notes", label: "Merknad-etikett", type: "input" },
      { key: "booking_placeholder_notes", label: "Merknad-placeholder", type: "input" },
      { key: "booking_btn_continue", label: "Fortsett-knapp", type: "input" },
      { key: "booking_btn_submit", label: "Send-knapp", type: "input" },
      { key: "booking_btn_back_home", label: "Tilbake hjem-knapp", type: "input" },
      { key: "booking_btn_back", label: "Tilbake-knapp", type: "input" },
      { key: "booking_no_date_selected", label: "Ingen dato valgt tekst", type: "input" },
      { key: "booking_loading_times", label: "Laster tider tekst", type: "input" },
      { key: "booking_no_times_available", label: "Ingen tider tilgjengelig tekst", type: "input" },
      { key: "booking_confirmation_text", label: "Bekreftelse tekst", type: "input" },
      { key: "booking_required_field_error", label: "Påkrevd felt feil", type: "input" },
      { key: "booking_submit_error", label: "Sendingsfeil", type: "input" },
      { key: "booking_submitting", label: "Sender tekst", type: "input" },
    ]},
  ];

  return (
    <div data-testid="content-tab">
      <h2 className="font-serif text-2xl mb-4">Nettstedsinnhold</h2>
      <Accordion type="multiple" className="space-y-4">
        {sections.map((section, index) => (
          <AccordionItem key={section.title} value={`section-${index}`} className="bg-card rounded-xl shadow-sm border-0">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <h3 className="font-serif text-lg text-left">{section.title}</h3>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-4">
              <div className="space-y-4">
                {section.fields.map(f => (
                  <div key={f.key}>
                    <label className="text-xs text-muted-foreground mb-1 block">{f.label}</label>
                    <div className="flex gap-2">
                      {f.type === "textarea" ? (
                        <Textarea
                          data-testid={`content-${f.key}`}
                          value={local[f.key] || ""}
                          onChange={e => setLocal({ ...local, [f.key]: e.target.value })}
                          className="flex-1 text-sm"
                        />
                      ) : (
                        <Input
                          data-testid={`content-${f.key}`}
                          value={local[f.key] || ""}
                          onChange={e => setLocal({ ...local, [f.key]: e.target.value })}
                          className="flex-1 text-sm"
                        />
                      )}
                      <Button data-testid={`save-content-${f.key}`} size="sm" variant="outline" className="rounded-lg" onClick={() => saveField(f.key)}>
                        <Save size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

/* ============ CUSTOMERS TAB ============ */
function CustomersTab({ customers, services, schedule, authFetch, onRefresh }) {
  const [expanded, setExpanded] = useState(null);
  const [customerBookings, setCustomerBookings] = useState({});
  const [bookingDialog, setBookingDialog] = useState(null);
  const [bookForm, setBookForm] = useState({ service_id: "", date: "", time: "", notes: "", admin_comment: "" });
  const [availableTimes, setAvailableTimes] = useState([]);
  const [editingComment, setEditingComment] = useState(null);
  const [commentText, setCommentText] = useState("");

  const toggleCustomer = async (email) => {
    if (expanded === email) { setExpanded(null); return; }
    setExpanded(email);
    if (!customerBookings[email]) {
      try {
        const res = await authFetch("get", `/customers/${encodeURIComponent(email)}/bookings`);
        setCustomerBookings(prev => ({ ...prev, [email]: res.data }));
      } catch { toast.error("Kunne ikke hente bookinger"); }
    }
  };

  const openBookDialog = (customer) => {
    setBookingDialog(customer);
    setBookForm({ service_id: "", date: "", time: "", notes: "", admin_comment: "" });
    setAvailableTimes([]);
  };

  const fetchTimes = async (date) => {
    setBookForm(prev => ({ ...prev, date, time: "" }));
    setAvailableTimes([]);
    if (!date) return;
    try {
      const res = await axios.get(`${API}/bookings/available?date=${date}`);
      setAvailableTimes(res.data.available_times);
    } catch { setAvailableTimes([]); }
  };

  const submitAdminBooking = async () => {
    if (!bookForm.service_id || !bookForm.date || !bookForm.time) {
      toast.error("Velg tjeneste, dato og tid"); return;
    }
    try {
      await authFetch("post", "/bookings/admin", {
        service_id: bookForm.service_id,
        date: bookForm.date,
        time: bookForm.time,
        customer_email: bookingDialog.email,
        customer_name: bookingDialog.name,
        customer_phone: bookingDialog.phone,
        notes: bookForm.notes,
        admin_comment: bookForm.admin_comment,
      });
      toast.success("Booking opprettet for kunden");
      setBookingDialog(null);
      // Refresh customer bookings
      const res = await authFetch("get", `/customers/${encodeURIComponent(bookingDialog.email)}/bookings`);
      setCustomerBookings(prev => ({ ...prev, [bookingDialog.email]: res.data }));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke opprette booking"); }
  };

  const saveComment = async (bookingId, email) => {
    try {
      await authFetch("put", `/bookings/${bookingId}/comment`, { admin_comment: commentText });
      toast.success("Kommentar lagret");
      setEditingComment(null);
      const res = await authFetch("get", `/customers/${encodeURIComponent(email)}/bookings`);
      setCustomerBookings(prev => ({ ...prev, [email]: res.data }));
      onRefresh();
    } catch { toast.error("Feil"); }
  };

  return (
    <div data-testid="customers-tab">
      <h2 className="font-serif text-2xl mb-4">Kunder ({customers.length})</h2>
      {customers.length === 0 ? <p className="text-muted-foreground">Ingen kunder registrert enna.</p> : (
        <div className="space-y-3">
          {customers.map(c => {
            const isOpen = expanded === c.email;
            const cBookings = customerBookings[c.email] || [];
            return (
              <div key={c.id || c.email} data-testid={`customer-${c.email}`} className="bg-card rounded-xl shadow-sm overflow-hidden">
                {/* Customer header */}
                <div
                  className="p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-6 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleCustomer(c.email)}
                  data-testid={`customer-toggle-${c.email}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{c.phone}</p>
                  <Button
                    data-testid={`book-for-customer-${c.email}`}
                    size="sm"
                    className="rounded-lg"
                    onClick={(e) => { e.stopPropagation(); openBookDialog(c); }}
                  >
                    <Plus size={14} className="mr-1" /> Book behandling
                  </Button>
                  <span className={`text-xs text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}>&#9662;</span>
                </div>

                {/* Expanded: bookings */}
                {isOpen && (
                  <div data-testid={`customer-bookings-${c.email}`} className="border-t border-border/50 bg-muted/20 px-4 py-4">
                    <h4 className="text-xs tracking-wide uppercase text-muted-foreground mb-3">Behandlingshistorikk ({cBookings.length})</h4>
                    {cBookings.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">Ingen bookinger funnet.</p>
                    ) : (
                      <div className="space-y-3">
                        {cBookings.map(b => (
                          <div key={b.id} data-testid={`cust-booking-${b.id}`} className="bg-card rounded-lg p-3 shadow-sm">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                              <span className="font-medium text-sm">{b.service_name}</span>
                              <span className="text-sm text-muted-foreground">{b.date} kl. {b.time}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || "bg-muted"}`}>
                                {STATUS_LABELS[b.status] || b.status}
                              </span>
                            </div>
                            {b.notes && <p className="text-xs text-muted-foreground mt-1 italic">Kundens merknad: {b.notes}</p>}
                            {/* Admin comment */}
                            <div className="mt-2 pt-2 border-t border-border/30">
                              {editingComment === b.id ? (
                                <div className="flex gap-2 items-start">
                                  <Textarea
                                    data-testid={`cust-comment-input-${b.id}`}
                                    value={commentText}
                                    onChange={e => setCommentText(e.target.value)}
                                    placeholder="Intern kommentar om behandlingen..."
                                    className="flex-1 text-xs min-h-[50px]"
                                  />
                                  <div className="flex flex-col gap-1">
                                    <Button data-testid={`cust-save-comment-${b.id}`} size="sm" className="rounded-lg h-7 px-2" onClick={() => saveComment(b.id, c.email)}>
                                      <Save size={10} />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="rounded-lg h-7 px-2" onClick={() => setEditingComment(null)}>
                                      <X size={10} />
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  data-testid={`cust-edit-comment-${b.id}`}
                                  onClick={() => { setEditingComment(b.id); setCommentText(b.admin_comment || ""); }}
                                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                                >
                                  <FileText size={10} />
                                  {b.admin_comment ? <span>{b.admin_comment}</span> : <span className="italic">Legg til kommentar...</span>}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Book for customer dialog */}
      <Dialog open={!!bookingDialog} onOpenChange={(open) => { if (!open) setBookingDialog(null); }}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Book behandling</DialogTitle>
            <DialogDescription>
              Opprett en booking for {bookingDialog?.name} ({bookingDialog?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Tjeneste *</label>
              <Select onValueChange={(v) => setBookForm({ ...bookForm, service_id: v })}>
                <SelectTrigger data-testid="admin-book-service" className="rounded-lg">
                  <SelectValue placeholder="Velg tjeneste" />
                </SelectTrigger>
                <SelectContent>
                  {services.filter(s => s.active).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} - {s.price} kr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Dato *</label>
              <Input
                data-testid="admin-book-date"
                type="date"
                value={bookForm.date}
                onChange={e => fetchTimes(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="rounded-lg"
              />
            </div>
            {bookForm.date && availableTimes.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Tid *</label>
                <div className="grid grid-cols-4 gap-2">
                  {availableTimes.map(t => (
                    <button
                      key={t}
                      data-testid={`admin-book-time-${t}`}
                      onClick={() => setBookForm({ ...bookForm, time: t })}
                      className={`rounded-lg py-2 text-xs font-medium transition-all ${
                        bookForm.time === t ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {bookForm.date && availableTimes.length === 0 && (
              <p className="text-sm text-muted-foreground">Ingen ledige tider denne dagen.</p>
            )}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Merknad til kunden</label>
              <Input data-testid="admin-book-notes" placeholder="Valgfritt" value={bookForm.notes} onChange={e => setBookForm({ ...bookForm, notes: e.target.value })} className="rounded-lg" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Intern kommentar</label>
              <Textarea data-testid="admin-book-comment" placeholder="Kun synlig for admin..." value={bookForm.admin_comment} onChange={e => setBookForm({ ...bookForm, admin_comment: e.target.value })} className="rounded-lg text-sm min-h-[60px]" />
            </div>
            <Button data-testid="admin-book-submit" onClick={submitAdminBooking} className="w-full rounded-lg">
              Opprett booking
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ ADMINS TAB ============ */
function AdminsTab({ admins, authFetch, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", email: "" });

  const createAdmin = async () => {
    if (!form.username || !form.password) { toast.error("Fyll ut brukernavn og passord"); return; }
    try {
      await authFetch("post", "/auth/admins", form);
      toast.success("Admin opprettet");
      setDialogOpen(false);
      setForm({ username: "", password: "", email: "" });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const deleteAdmin = async (id) => {
    try { await authFetch("delete", `/auth/admins/${id}`); toast.success("Admin slettet"); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  return (
    <div data-testid="admins-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl">Administratorer</h2>
        <Button data-testid="add-admin-btn" size="sm" onClick={() => setDialogOpen(true)} className="rounded-lg">
          <UserPlus size={14} className="mr-1" /> Ny admin
        </Button>
      </div>
      <div className="space-y-2">
        {admins.map(a => (
          <div key={a.id} data-testid={`admin-item-${a.id}`} className="bg-card rounded-xl p-4 flex items-center gap-4 shadow-sm">
            <div className="flex-1">
              <p className="font-medium text-sm">{a.username}</p>
              <p className="text-xs text-muted-foreground">{a.email || "Ingen e-post"}</p>
            </div>
            <Button data-testid={`delete-admin-${a.id}`} variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => deleteAdmin(a.id)}>
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">Ny administrator</DialogTitle>
            <DialogDescription>Legg til en ny admin-bruker.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="new-admin-username" placeholder="Brukernavn" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} />
            <Input data-testid="new-admin-password" type="password" placeholder="Passord" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
            <Input data-testid="new-admin-email" placeholder="E-post (valgfritt)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <Button data-testid="save-admin-btn" onClick={createAdmin} className="w-full rounded-lg">Opprett admin</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
